import React, { useState, useEffect } from 'react';
import { FaStar, FaCheckCircle, FaTimes, FaTrash, FaEye, FaEyeSlash, FaSpinner, FaEdit } from 'react-icons/fa';
import apiService from '../../services/api';

const AdminReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editingReview, setEditingReview] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        rating: 5,
        review: '',
        is_verified: false,
        created_at: ''
    });

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const data = await apiService.getAllReviews();
            setReviews(data);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            setError('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    const handleApprovalToggle = async (reviewId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await apiService.updateReviewStatus(reviewId, newStatus);

            setReviews(reviews.map(review =>
                review.id === reviewId
                    ? { ...review, is_approved: newStatus }
                    : review
            ));

            setMessage(`Review ${newStatus ? 'approved' : 'unapproved'} successfully`);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setError(error.message || 'Failed to update review status');
            setTimeout(() => setError(''), 5000);
        }
    };

    const handleEdit = (review) => {
        setEditingReview(review);
        setEditForm({
            name: review.name,
            email: review.email || '',
            rating: review.rating,
            review: review.review,
            is_verified: review.is_verified,
            created_at: review.created_at.split('T')[0] // Convert to YYYY-MM-DD format for date input
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditRatingClick = (rating) => {
        setEditForm(prev => ({ ...prev, rating }));
    };

    const handleUpdateReview = async (e) => {
        e.preventDefault();

        try {
            const updatedReview = await apiService.updateReview(editingReview.id, editForm);

            setReviews(reviews.map(review =>
                review.id === editingReview.id ? updatedReview : review
            ));

            setMessage('Review updated successfully');
            setEditingReview(null);
            setEditForm({ name: '', email: '', rating: 5, review: '', is_verified: false });
            setTimeout(() => setMessage(''), 3000);

        } catch (error) {
            setError(error.message || 'Failed to update review');
            setTimeout(() => setError(''), 5000);
        }
    };

    const handleCancelEdit = () => {
        setEditingReview(null);
        setEditForm({ name: '', email: '', rating: 5, review: '', is_verified: false, created_at: '' });
    };

    const handleDelete = async (reviewId) => {
        if (!window.confirm('Are you sure you want to delete this review?')) {
            return;
        }

        try {
            await apiService.deleteReview(reviewId);
            setReviews(reviews.filter(review => review.id !== reviewId));
            setMessage('Review deleted successfully');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setError(error.message || 'Failed to delete review');
            setTimeout(() => setError(''), 5000);
        }
    };

    const renderStars = (rating, interactive = false, onRatingClick = null) => {
        return [...Array(5)].map((_, index) => (
            <FaStar
                key={index}
                className={`star ${index < rating ? 'star-filled' : 'star-empty'} ${interactive ? 'interactive' : ''}`}
                onClick={() => interactive && onRatingClick && onRatingClick(index + 1)}
            />
        ));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="admin-reviews">
                <div className="loading-container">
                    <FaSpinner className="spinner" />
                    <p>Loading reviews...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-reviews">
            <div className="page-header">
                <h1>Review Management</h1>
                <p>Manage customer reviews and feedback</p>
            </div>

            {message && (
                <div className="alert alert-success">
                    <FaCheckCircle />
                    {message}
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    {error}
                </div>
            )}

            <div className="stats-cards">
                <div className="stat-card">
                    <h3>Total Reviews</h3>
                    <p className="stat-number">{reviews.length}</p>
                </div>
                <div className="stat-card">
                    <h3>Approved</h3>
                    <p className="stat-number approved">{reviews.filter(r => r.is_approved).length}</p>
                </div>
                <div className="stat-card">
                    <h3>Pending</h3>
                    <p className="stat-number pending">{reviews.filter(r => !r.is_approved).length}</p>
                </div>
                <div className="stat-card">
                    <h3>Verified</h3>
                    <p className="stat-number verified">{reviews.filter(r => r.is_verified).length}</p>
                </div>
            </div>

            <div className="reviews-table">
                {reviews.length === 0 ? (
                    <div className="empty-state">
                        <p>No reviews found.</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Reviewer</th>
                                    <th>Rating</th>
                                    <th>Review</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviews.map((review) => (
                                    <tr key={review.id} className={!review.is_approved ? 'pending-row' : ''}>
                                        <td>
                                            <div className="reviewer-info">
                                                <strong>{review.name}</strong>
                                                {review.email && (
                                                    <div className="email">{review.email}</div>
                                                )}
                                                {review.is_verified && (
                                                    <span className="verified-badge">
                                                        <FaCheckCircle />
                                                        Verified
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="rating">
                                                {renderStars(review.rating)}
                                                <span className="rating-number">({review.rating})</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="review-text">
                                                {review.review.length > 100
                                                    ? `${review.review.substring(0, 100)}...`
                                                    : review.review
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${review.is_approved ? 'approved' : 'pending'}`}>
                                                {review.is_approved ? 'Approved' : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="date-info">
                                                <div>{formatDate(review.created_at)}</div>
                                                {review.updated_at !== review.created_at && (
                                                    <small>Updated: {formatDate(review.updated_at)}</small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="actions">
                                                <button
                                                    className="btn btn-sm btn-info"
                                                    onClick={() => handleEdit(review)}
                                                    title="Edit Review"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${review.is_approved ? 'btn-warning' : 'btn-success'}`}
                                                    onClick={() => handleApprovalToggle(review.id, review.is_approved)}
                                                    title={review.is_approved ? 'Unapprove' : 'Approve'}
                                                >
                                                    {review.is_approved ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDelete(review.id)}
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Review Modal */}
            {editingReview && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Edit Review</h2>
                            <button
                                className="close-btn"
                                onClick={handleCancelEdit}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateReview} className="edit-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="edit_name">Name *</label>
                                    <input
                                        type="text"
                                        id="edit_name"
                                        name="name"
                                        value={editForm.name}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="edit_email">Email</label>
                                    <input
                                        type="email"
                                        id="edit_email"
                                        name="email"
                                        value={editForm.email}
                                        onChange={handleEditInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="edit_date">Review Date *</label>
                                    <input
                                        type="date"
                                        id="edit_date"
                                        name="created_at"
                                        value={editForm.created_at}
                                        onChange={handleEditInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Rating *</label>
                                <div className="rating-input">
                                    {renderStars(editForm.rating, true, handleEditRatingClick)}
                                    <span className="rating-text">({editForm.rating} star{editForm.rating !== 1 ? 's' : ''})</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="edit_review">Review *</label>
                                <textarea
                                    id="edit_review"
                                    name="review"
                                    value={editForm.review}
                                    onChange={handleEditInputChange}
                                    required
                                    rows="4"
                                    minLength="10"
                                />
                                <small className="character-count">
                                    {editForm.review.length}/10 minimum characters
                                </small>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="is_verified"
                                        checked={editForm.is_verified}
                                        onChange={handleEditInputChange}
                                    />
                                    <span className="checkmark"></span>
                                    Mark as verified customer
                                </label>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Update Review
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .admin-reviews {
                    padding: 24px;
                }

                .page-header {
                    margin-bottom: 32px;
                }

                .page-header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 8px;
                }

                .page-header p {
                    color: #64748b;
                    font-size: 16px;
                }

                .loading-container {
                    text-align: center;
                    padding: 80px 0;
                }

                .spinner {
                    animation: spin 1s linear infinite;
                    font-size: 24px;
                    color: #2563eb;
                    margin-bottom: 16px;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .alert {
                    padding: 16px 20px;
                    border-radius: 8px;
                    margin-bottom: 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-weight: 500;
                }

                .alert-success {
                    background: #d1fae5;
                    color: #065f46;
                    border: 1px solid #a7f3d0;
                }

                .alert-error {
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fca5a5;
                }

                .stats-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                    margin-bottom: 32px;
                }

                .stat-card {
                    background: white;
                    padding: 24px;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    text-align: center;
                }

                .stat-card h3 {
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .stat-number {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0;
                }

                .stat-number.approved {
                    color: #059669;
                }

                .stat-number.pending {
                    color: #d97706;
                }

                .stat-number.verified {
                    color: #2563eb;
                }

                .reviews-table {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                }

                .empty-state {
                    text-align: center;
                    padding: 80px 20px;
                }

                .empty-state p {
                    color: #64748b;
                    font-size: 16px;
                }

                .table-container {
                    overflow-x: auto;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                thead {
                    background: #f8fafc;
                }

                th {
                    padding: 16px 12px;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 1px solid #e5e7eb;
                    font-size: 14px;
                }

                td {
                    padding: 16px 12px;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: top;
                }

                .pending-row {
                    background: #fefce8;
                }

                .reviewer-info strong {
                    display: block;
                    font-weight: 600;
                    color: #1a202c;
                    margin-bottom: 4px;
                }

                .email {
                    font-size: 14px;
                    color: #64748b;
                    margin-bottom: 4px;
                }

                .verified-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: #dbeafe;
                    color: #1d4ed8;
                    padding: 2px 6px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                }

                .verified-badge svg {
                    font-size: 10px;
                }

                .rating {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .star-filled {
                    color: #f59e0b;
                    font-size: 14px;
                }

                .star-empty {
                    color: #e5e7eb;
                    font-size: 14px;
                }

                .rating-number {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                }

                .review-text {
                    max-width: 300px;
                    line-height: 1.5;
                    color: #374151;
                    font-size: 14px;
                }

                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .status-badge.approved {
                    background: #d1fae5;
                    color: #065f46;
                }

                .status-badge.pending {
                    background: #fef3c7;
                    color: #92400e;
                }

                .date-info {
                    font-size: 14px;
                }

                .date-info small {
                    display: block;
                    color: #64748b;
                    font-size: 12px;
                    margin-top: 2px;
                }

                .actions {
                    display: flex;
                    gap: 8px;
                }

                .btn {
                    padding: 8px 12px;
                    border-radius: 6px;
                    border: none;
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }

                .btn-sm {
                    padding: 6px 8px;
                    min-width: 32px;
                    height: 32px;
                }

                .btn-success {
                    background: #059669;
                    color: white;
                }

                .btn-success:hover {
                    background: #047857;
                }

                .btn-warning {
                    background: #d97706;
                    color: white;
                }

                .btn-warning:hover {
                    background: #b45309;
                }

                .btn-danger {
                    background: #dc2626;
                    color: white;
                }

                .btn-danger:hover {
                    background: #b91c1c;
                }

                .btn-info {
                    background: #0369a1;
                    color: white;
                }

                .btn-info:hover {
                    background: #075985;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 24px 0 24px;
                    margin-bottom: 24px;
                }

                .modal-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 18px;
                    color: #64748b;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    background: #f1f5f9;
                    color: #1a202c;
                }

                .edit-form {
                    padding: 0 24px 24px 24px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #374151;
                    font-size: 14px;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    font-family: inherit;
                }

                .form-group input:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                .rating-input {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-top: 8px;
                }

                .star.interactive {
                    cursor: pointer;
                    font-size: 24px;
                }

                .star.interactive:hover {
                    transform: scale(1.1);
                }

                .rating-text {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                }

                .character-count {
                    display: block;
                    margin-top: 4px;
                    font-size: 12px;
                    color: #64748b;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    cursor: pointer;
                    font-weight: 500;
                    color: #374151;
                }

                .checkbox-label input[type="checkbox"] {
                    width: auto;
                    margin: 0;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 32px;
                    padding-top: 20px;
                    border-top: 1px solid #e5e7eb;
                }

                @media (max-width: 768px) {
                    .admin-reviews {
                        padding: 16px;
                    }

                    .stats-cards {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .page-header h1 {
                        font-size: 24px;
                    }

                    .table-container {
                        font-size: 14px;
                    }

                    th, td {
                        padding: 12px 8px;
                    }

                    .review-text {
                        max-width: 200px;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }
                }

                @media (max-width: 480px) {
                    .stats-cards {
                        grid-template-columns: 1fr;
                    }

                    .actions {
                        flex-direction: column;
                        gap: 4px;
                    }

                    .modal-content {
                        margin: 20px;
                        max-height: calc(100vh - 40px);
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminReviews;